import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "../components/Layout";
import BackToProjectBanner from "../components/BackToProjectBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import DatePickerField from "../components/DatePickerField";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus, Trash2, Ruler, ChevronDown, ChevronRight, Save, Loader2, Copy, Upload, Sparkles,
} from "lucide-react";
import { useData } from "../contexts/DataContext";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const ROOF_FIELDS = [
  { key: "waste_factor", label: "Waste Factor", unit: "%" },
  { key: "total_area", label: "Total Area", unit: "SF" },
  { key: "rakes", label: "Rakes", unit: "LF" },
  { key: "eaves", label: "Eaves", unit: "LF" },
  { key: "ridges", label: "Ridges", unit: "LF" },
  { key: "valleys", label: "Valleys", unit: "LF" },
  { key: "hips", label: "Hips", unit: "LF" },
  { key: "boots", label: "Boots", unit: "" },
  { key: "transitions", label: "Transitions", unit: "LF" },
  { key: "osb", label: "OSB", unit: "" },
  { key: "sealant", label: "Sealant", unit: "" },
  { key: "spray", label: "Spray", unit: "" },
  { key: "flashing", label: "Flashing", unit: "" },
  { key: "delivery", label: "Delivery", unit: "" },
];

const emptyStructure = () => ({
  name: "Structure 1",
  waste_factor: 0, total_area: 0, rakes: 0, eaves: 0, ridges: 0,
  valleys: 0, hips: 0, boots: 0, transitions: 0, osb: 5,
  sealant: 0, spray: 0, flashing: 0, drip_edge: 0,
  delivery: 1, labor_low: 0, labor_mid: 0, labor_high: 0,
  pitch_factor: "mid",
});

export default function MeasurementCalculator({ embeddedReportId, onClose } = {}) {
  const navigate = useNavigate();
  const params = useParams();
  const reportId = embeddedReportId || params.reportId;
  const isEmbedded = Boolean(embeddedReportId);
  const { projects, fetchProjects, contacts } = useData();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedStructures, setExpandedStructures] = useState({});

  useEffect(() => {
    fetchReports();
    fetchProjects();
  }, [fetchProjects]);

  // Deep-link: if URL contains a reportId, fetch and open that specific report.
  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/roof-reports/${reportId}`);
        if (!cancelled) {
          setActiveReport(res.data);
          setExpandedStructures({ 0: true });
        }
      } catch {
        if (!cancelled) toast.error("Report not found");
      }
    })();
    return () => { cancelled = true; };
  }, [reportId]);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API_URL}/roof-reports`);
      setReports(res.data);
    } catch {}
    setLoading(false);
  };

  const handleCreateReport = async () => {
    try {
      const res = await axios.post(`${API_URL}/roof-reports`, {
        name: `Roof Report ${reports.length + 1}`,
        structures: [emptyStructure()],
        date: new Date().toISOString().split("T")[0],
      });
      setReports(prev => [res.data, ...prev]);
      setActiveReport(res.data);
      setExpandedStructures({ 0: true });
      toast.success("Report created");
    } catch { toast.error("Failed to create report"); }
  };

  const handleSaveReport = async () => {
    if (!activeReport) return;
    setSaving(true);
    try {
      // Sanitize: convert empty strings to 0 for numeric fields before saving
      const sanitized = {
        ...activeReport,
        structures: activeReport.structures.map(s => {
          const clean = { ...s };
          Object.keys(clean).forEach(k => {
            if (k !== 'name' && clean[k] === '') clean[k] = 0;
          });
          return clean;
        }),
      };
      const res = await axios.put(`${API_URL}/roof-reports/${activeReport.id}`, sanitized);
      setReports(prev => prev.map(r => r.id === activeReport.id ? res.data : r));
      toast.success("Report saved");
    } catch (err) { console.error("Save error:", err?.response?.data || err.message); toast.error("Failed to save"); }
    setSaving(false);
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [importing, setImporting] = useState(false);
  const pdfInputRef = useRef(null);

  // Merge parser output into the active report's first structure. Existing
  // non-zero values are PRESERVED so we don't clobber manual edits — this is
  // an "auto-fill blanks", not a "overwrite everything".
  const handleImportPdf = async (fileList) => {
    const file = fileList && fileList[0];
    if (!file || !activeReport) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please pick a PDF file");
      return;
    }
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (activeReport.project_id) form.append("project_id", activeReport.project_id);
      if (activeReport.contact_id) form.append("contact_id", activeReport.contact_id);
      const { data } = await axios.post(`${API_URL}/roof-reports/parse-pdf`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const parsed = data?.parsed || {};
      const src = (parsed.structures && parsed.structures[0]) || {};
      const numericKeys = [
        "total_area", "rakes", "eaves", "ridges", "valleys", "hips",
        "boots", "transitions", "osb", "sealant", "spray", "flashing",
        "drip_edge", "delivery", "labor_low", "labor_mid", "labor_high",
        "waste_factor",
      ];
      setActiveReport(prev => {
        if (!prev) return prev;
        const structs = [...(prev.structures || [])];
        const s0 = { ...(structs[0] || {}) };
        // Fill only fields that are blank / zero — preserves manual edits.
        for (const k of numericKeys) {
          const incoming = parseFloat(src[k]);
          const existing = parseFloat(s0[k]);
          if (Number.isFinite(incoming) && incoming > 0 && !(existing > 0)) {
            s0[k] = incoming;
          }
        }
        // Standard install defaults — auto-add 5 OSB, 1 Delivery, 5 Sealants
        // on any freshly imported PDF (only fills blanks; manual edits win).
        const defaults = { osb: 5, delivery: 1, sealant: 5 };
        for (const [k, v] of Object.entries(defaults)) {
          if (!(parseFloat(s0[k]) > 0)) s0[k] = v;
        }
        if (src.name && (!s0.name || s0.name === "Structure 1")) {
          s0.name = src.name;
        }
        structs[0] = s0;
        return {
          ...prev,
          structures: structs,
          notes: prev.notes || parsed.notes || "",
        };
      });
      setExpandedStructures(prev => ({ ...prev, 0: true }));
      const savedFile = data?.saved_file;
      if (savedFile) {
        toast.success("Report auto-filled and PDF saved to project files");
      } else if (!activeReport.project_id) {
        toast.success("Report auto-filled (link a project to also save the PDF)");
      } else {
        toast.success("Report auto-filled");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to parse PDF";
      toast.error(msg);
    } finally {
      setImporting(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const handleDeleteReport = async (id) => {
    try {
      await axios.delete(`${API_URL}/roof-reports/${id}`);
      setReports(prev => prev.filter(r => r.id !== id));
      if (activeReport?.id === id) setActiveReport(null);
      setDeleteConfirmId(null);
      toast.success("Moved to trash");
    } catch { toast.error("Failed to delete"); }
  };

  const updateField = (structIdx, field, value) => {
    setActiveReport(prev => {
      const structs = [...prev.structures];
      const updated = { ...structs[structIdx] };
      if (field === "pitch_factor") {
        updated[field] = value;
      } else {
        updated[field] = value === '' ? '' : (parseFloat(value) || 0);
      }
      
      // Auto-fill labor fields when area/waste/eaves/rakes/ridges/hips change or pitch changes
      const laborDeps = ["total_area", "waste_factor", "eaves", "rakes", "ridges", "hips", "pitch_factor"];
      if (laborDeps.includes(field)) {
        const s = updated;
        const waste = parseFloat(s.waste_factor) || 0;
        const area = parseFloat(s.total_area) || 0;
        const eaves = parseFloat(s.eaves) || 0;
        const rakes = parseFloat(s.rakes) || 0;
        const ridges = parseFloat(s.ridges) || 0;
        const hips = parseFloat(s.hips) || 0;
        const fieldSq = area * (1 + waste / 100) / 100;
        const starterSq = ((eaves + rakes) * (1 + waste / 100) / 120) / 3;
        const hrSq = ((hips + ridges) / 20) / 3;
        const totalSq = Math.round((fieldSq + starterSq + hrSq) * 100) / 100;
        const pitch = s.pitch_factor || "mid";
        updated.labor_low = pitch === "low" ? totalSq : 0;
        updated.labor_mid = pitch === "mid" ? totalSq : 0;
        updated.labor_high = pitch === "high" ? totalSq : 0;
      }
      
      structs[structIdx] = updated;
      return { ...prev, structures: structs };
    });
  };

  const updateStructureName = (structIdx, name) => {
    setActiveReport(prev => {
      const structs = [...prev.structures];
      structs[structIdx] = { ...structs[structIdx], name };
      return { ...prev, structures: structs };
    });
  };

  const addStructure = () => {
    const idx = activeReport.structures.length;
    setActiveReport(prev => ({
      ...prev,
      structures: [...prev.structures, { ...emptyStructure(), name: `Structure ${idx + 1}` }],
    }));
    setExpandedStructures(prev => ({ ...prev, [idx]: true }));
  };

  const removeStructure = (idx) => {
    if (activeReport.structures.length <= 1) return;
    setActiveReport(prev => ({
      ...prev,
      structures: prev.structures.filter((_, i) => i !== idx),
    }));
  };

  const toggleStructure = (idx) => {
    setExpandedStructures(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Report list view
  if (!activeReport) {
    return (
      <div className="min-h-screen">
        <Header title="Roof Measurements">
          <Button className="bg-[#0F4C81] hover:bg-[#0D4270] h-10" onClick={handleCreateReport} data-testid="new-roof-report-btn">
            <Plus className="w-4 h-4 mr-2" />New Report
          </Button>
        </Header>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" /></div>
          ) : reports.length > 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {reports.map(report => {
                const project = projects.find(p => p.id === report.project_id);
                const contact = contacts.find(c => c.id === report.contact_id);
                return (
                  <div
                    key={report.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3 hover:bg-slate-50 cursor-pointer group transition-colors"
                    onClick={() => { setActiveReport(report); setExpandedStructures({ 0: true }); }}
                    data-testid={`roof-report-${report.id}`}
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <Ruler className="w-5 h-5 text-[#0F4C81] flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 text-sm sm:text-base truncate">{report.name}</p>
                        {(contact || project) && (
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {contact?.name}
                            {contact && project?.project_address && " · "}
                            {project?.project_address}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 sm:flex-shrink-0 pl-8 sm:pl-0 flex-wrap">
                      <Badge className={report.status === "complete" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                        {report.status}
                      </Badge>
                      <span className="text-xs text-slate-400 whitespace-nowrap">{report.structures?.length || 0} structure{(report.structures?.length || 0) === 1 ? "" : "s"}</span>
                      <span className="text-xs text-slate-400 whitespace-nowrap">{report.date}</span>
                      {deleteConfirmId === report.id ? (
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="text-red-600 text-xs h-7 px-2" onClick={() => handleDeleteReport(report.id)}>Delete</Button>
                          <Button variant="ghost" size="sm" className="text-slate-400 text-xs h-7 px-2" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" className="text-red-400 sm:opacity-0 sm:group-hover:opacity-100 h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(report.id); }} data-testid={`delete-roof-report-${report.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Ruler className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">No roof reports yet</p>
              <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={handleCreateReport}>Create First Report</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Active report form view
  const project = projects.find(p => p.id === activeReport.project_id);

  return (
    <div className="min-h-screen bg-slate-50">
      <BackToProjectBanner />
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { if (isEmbedded && onClose) { onClose(); } else if (reportId) { navigate(-1); } else { setActiveReport(null); } }} data-testid="back-to-reports-btn">
              <ChevronRight className="w-4 h-4 rotate-180 mr-1" />Back
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <Input
              value={activeReport.name}
              onChange={(e) => setActiveReport(prev => ({ ...prev, name: e.target.value }))}
              className="h-9 w-48 text-sm font-semibold border-0 bg-transparent focus-visible:ring-1 px-2"
              data-testid="report-name-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => handleImportPdf(e.target.files)}
              data-testid="import-pdf-input"
            />
            <Button
              variant="outline"
              size="sm"
              className="h-9 border-[#0F4C81]/30 text-[#0F4C81] hover:bg-[#0F4C81]/5"
              onClick={() => pdfInputRef.current && pdfInputRef.current.click()}
              disabled={importing}
              title="Auto-fill from GAF QuickMeasure / Hover / EagleView PDF"
              data-testid="import-pdf-btn"
            >
              {importing ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Reading…</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-1" />Import PDF</>
              )}
            </Button>
            <Select
              value={activeReport.status}
              onValueChange={(v) => setActiveReport(prev => ({ ...prev, status: v }))}
            >
              <SelectTrigger className="h-9 w-36" data-testid="report-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="incomplete">Incomplete</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
              </SelectContent>
            </Select>
            <Button className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" onClick={handleSaveReport} disabled={saving} data-testid="save-report-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save
            </Button>
            {deleteConfirmId === activeReport.id ? (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-9 text-red-600 text-xs" onClick={() => handleDeleteReport(activeReport.id)}>Confirm Delete</Button>
                <Button variant="ghost" size="sm" className="h-9 text-slate-400 text-xs" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" className="h-9 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirmId(activeReport.id)} data-testid="delete-report-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4">
        {/* Report Info */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            value={activeReport.project_id || "none"}
            onValueChange={(v) => setActiveReport(prev => ({ ...prev, project_id: v === "none" ? "" : v }))}
          >
            <SelectTrigger className="h-10 bg-white" data-testid="report-project-select"><SelectValue placeholder="Link to project" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No project</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_address || p.id.slice(0, 8)}</SelectItem>)}
            </SelectContent>
          </Select>
          <DatePickerField
            value={activeReport.date || ""}
            onChange={(v) => setActiveReport(prev => ({ ...prev, date: v }))}
            height="h-10"
            className="bg-white"
          />
        </div>

        {/* Structures */}
        {activeReport.structures.map((structure, idx) => (
          <Card key={idx} className="overflow-hidden">
            {/* Structure Header */}
            <div
              className="flex items-center justify-between px-4 py-3 bg-[#0F4C81] text-white cursor-pointer select-none"
              onClick={() => toggleStructure(idx)}
              data-testid={`structure-header-${idx}`}
            >
              <div className="flex items-center gap-2">
                {expandedStructures[idx] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <Input
                  value={structure.name}
                  onChange={(e) => { e.stopPropagation(); updateStructureName(idx, e.target.value); }}
                  onClick={(e) => e.stopPropagation()}
                  className="h-7 w-40 text-sm font-semibold bg-white/10 border-white/20 text-white placeholder:text-white/50 px-2"
                />
              </div>
              <div className="flex items-center gap-2">
                {activeReport.structures.length > 1 && (
                  <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 h-7 px-2" onClick={(e) => { e.stopPropagation(); removeStructure(idx); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {/* Structure Fields */}
            {expandedStructures[idx] && (
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {ROOF_FIELDS.map((field) => (
                    <div
                      key={field.key}
                      className="flex items-center justify-between px-4 py-2 hover:bg-slate-50 transition-colors"
                      data-testid={`field-${idx}-${field.key}`}
                    >
                      <span className="text-sm text-slate-700 font-medium">
                        {field.label}
                        {field.unit && <span className="text-slate-400 font-normal ml-1">({field.unit})</span>}
                      </span>
                      <Input
                        type="number"
                        step="any"
                        value={structure[field.key] || ""}
                        onChange={(e) => updateField(idx, field.key, e.target.value)}
                        placeholder="—"
                        className="w-28 h-8 text-right text-sm border-slate-200 focus-visible:ring-[#0F4C81]"
                        data-testid={`input-${idx}-${field.key}`}
                      />
                    </div>
                  ))}
                  {/* Pitch Factor Selector + Labor */}
                  <div className="px-4 py-3 bg-slate-50 space-y-2" data-testid={`pitch-section-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700 font-semibold">Pitch Factor</span>
                      <div className="flex rounded-lg overflow-hidden border border-slate-300">
                        {[
                          { value: "low", label: "Low", sub: "7/12" },
                          { value: "mid", label: "Mid", sub: "7-9/12" },
                          { value: "high", label: "High", sub: "9/12+" },
                        ].map(opt => {
                          const isActive = (structure.pitch_factor || "mid") === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => updateField(idx, "pitch_factor", opt.value)}
                              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${isActive ? "bg-[#0F4C81] text-white" : "bg-white text-slate-600 hover:bg-slate-100"} ${opt.value !== "low" ? "border-l border-slate-300" : ""}`}
                              data-testid={`pitch-${opt.value}-${idx}`}
                            >
                              {opt.label}
                              <span className={`block text-[10px] font-normal ${isActive ? "text-blue-200" : "text-slate-400"}`}>{opt.sub}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">
                        Labor <span className="text-slate-400 text-xs">({(structure.pitch_factor || "mid") === "low" ? "7/12" : (structure.pitch_factor || "mid") === "mid" ? "7-9/12" : "9/12+"})</span>
                      </span>
                      <Input
                        type="number"
                        step="any"
                        value={structure[`labor_${structure.pitch_factor || "mid"}`] || ""}
                        onChange={(e) => updateField(idx, `labor_${structure.pitch_factor || "mid"}`, e.target.value)}
                        placeholder="—"
                        className="w-28 h-8 text-right text-sm border-slate-200 focus-visible:ring-[#0F4C81]"
                        data-testid={`input-${idx}-labor`}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}

        {/* Add Structure */}
        <Button variant="outline" className="w-full h-10 border-dashed" onClick={addStructure} data-testid="add-structure-btn">
          <Plus className="w-4 h-4 mr-2" />Add Structure
        </Button>

        {/* Auto-Calculated Totals */}
        {activeReport.structures.length > 0 && (
          <Card className="border-2 border-[#0F4C81] overflow-hidden" data-testid="measurement-totals">
            <div className="px-4 py-3 bg-[#0F4C81]">
              <p className="text-white font-bold text-sm">
                {activeReport.structures.length > 1 ? "Combined Totals (All Structures)" : "Totals"}
              </p>
            </div>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {(() => {
                  const totals = {};
                  ROOF_FIELDS.forEach(f => {
                    totals[f.key] = activeReport.structures.reduce((sum, s) => sum + (parseFloat(s[f.key]) || 0), 0);
                  });
                  const numStructures = activeReport.structures.length || 1;
                  const avgWaste = totals.waste_factor / numStructures;

                  // Calculated breakdowns
                  const fieldSquares = totals.total_area * (1 + avgWaste / 100) / 100;
                  const starterLF = (totals.eaves + totals.rakes) * (1 + avgWaste / 100);
                  const starterBundles = starterLF / 120;
                  const starterSquares = starterBundles / 3;
                  const hipRidgeBundles = (totals.hips + totals.ridges) / 20;
                  const hipRidgeSquares = hipRidgeBundles / 3;
                  const totalSquares = fieldSquares + starterSquares + hipRidgeSquares;
                  const dripEdgeCalc = (totals.eaves + totals.rakes) * (1 + avgWaste / 100) / 10;

                  // Fields to display (exclude waste_factor and drip_edge from raw list)
                  const rawFields = ROOF_FIELDS.filter(f => f.key !== "waste_factor" && f.key !== "drip_edge");

                  return (
                    <>
                      {/* Total Square Count */}
                      <div className="flex items-center justify-between px-4 py-3 bg-[#0F4C81]/10" data-testid="total-squares-row">
                        <span className="text-sm font-bold text-[#0F4C81]">Total Squares</span>
                        <span className="text-lg font-bold text-[#0F4C81]">{totalSquares.toFixed(2)} sq</span>
                      </div>

                      {/* Field Squares */}
                      <div className="flex items-center justify-between px-4 py-2 bg-blue-50/60" data-testid="field-squares-row">
                        <span className="text-sm font-medium text-[#0F4C81]">Field Squares</span>
                        <span className="text-sm font-semibold text-[#0F4C81]">{fieldSquares.toFixed(2)} sq</span>
                      </div>
                      <div className="flex items-center justify-between px-4 py-1.5 bg-blue-50/30">
                        <span className="text-xs text-slate-400 pl-3">({totals.total_area.toFixed(0)} SF + {avgWaste.toFixed(0)}% waste) / 100</span>
                      </div>

                      {/* Starter */}
                      <div className="flex items-center justify-between px-4 py-2 bg-blue-50/60" data-testid="starter-squares-row">
                        <span className="text-sm font-medium text-[#0F4C81]">Starter</span>
                        <span className="text-sm font-semibold text-[#0F4C81]">{starterBundles.toFixed(2)} bdl &rarr; {starterSquares.toFixed(2)} sq</span>
                      </div>
                      <div className="flex items-center justify-between px-4 py-1.5 bg-blue-50/30">
                        <span className="text-xs text-slate-400 pl-3">(Eaves {totals.eaves.toFixed(0)} + Rakes {totals.rakes.toFixed(0)} + {avgWaste.toFixed(0)}% waste) / 120 = bdl, bdl / 3 = sq</span>
                      </div>

                      {/* Hip / Ridge */}
                      <div className="flex items-center justify-between px-4 py-2 bg-blue-50/60" data-testid="hip-ridge-squares-row">
                        <span className="text-sm font-medium text-[#0F4C81]">Hip / Ridge</span>
                        <span className="text-sm font-semibold text-[#0F4C81]">{hipRidgeBundles.toFixed(2)} bdl &rarr; {hipRidgeSquares.toFixed(2)} sq</span>
                      </div>
                      <div className="flex items-center justify-between px-4 py-1.5 bg-blue-50/30">
                        <span className="text-xs text-slate-400 pl-3">(Hips {totals.hips.toFixed(0)} + Ridges {totals.ridges.toFixed(0)}) / 20 = bdl, bdl / 3 = sq</span>
                      </div>

                      {/* Divider */}
                      <div className="h-px bg-[#0F4C81]/20" />

                      {/* Calculated Drip Edge */}
                      <div className="flex items-center justify-between px-4 py-2 bg-amber-50/60" data-testid="drip-edge-calc-row">
                        <span className="text-sm font-medium text-amber-800">Drip Edge</span>
                        <span className="text-sm font-semibold text-amber-800">{dripEdgeCalc.toFixed(1)} pcs</span>
                      </div>
                      <div className="flex items-center justify-between px-4 py-1.5 bg-amber-50/30">
                        <span className="text-xs text-slate-400 pl-3">(Eaves {totals.eaves.toFixed(0)} + Rakes {totals.rakes.toFixed(0)} + {avgWaste.toFixed(0)}% waste) / 10</span>
                      </div>

                      {/* Divider */}
                      <div className="h-px bg-slate-200" />

                      {/* Raw measurement totals */}
                      {rawFields.map(field => (
                        <div key={field.key} className="flex items-center justify-between px-4 py-2 hover:bg-slate-50">
                          <span className="text-sm text-slate-600">
                            {field.label}
                            {field.unit && <span className="text-slate-400 ml-1">({field.unit})</span>}
                          </span>
                          <span className={`text-sm font-semibold ${totals[field.key] > 0 ? "text-slate-800" : "text-slate-300"}`}>
                            {totals[field.key] > 0 ? totals[field.key].toFixed(field.key === "total_area" ? 0 : 1) : "—"}
                          </span>
                        </div>
                      ))}
                      {/* Labor by pitch */}
                      {(() => {
                        // Determine active pitch from structures
                        const pitches = activeReport.structures.map(st => st.pitch_factor || "mid");
                        const uniquePitches = [...new Set(pitches)];
                        const pitchLabel = uniquePitches.length === 1
                          ? (uniquePitches[0] === "low" ? "Low (7/12)" : uniquePitches[0] === "high" ? "High (9/12+)" : "Mid (7-9/12)")
                          : "Mixed";
                        const laborKey = uniquePitches.length === 1 ? `labor_${uniquePitches[0]}` : null;
                        const activeVal = laborKey
                          ? activeReport.structures.reduce((s, st) => s + (parseFloat(st[laborKey]) || 0), 0)
                          : activeReport.structures.reduce((s, st) => {
                              const p = st.pitch_factor || "mid";
                              return s + (parseFloat(st[`labor_${p}`]) || 0);
                            }, 0);
                        return (
                          <div className="flex items-center justify-between px-4 py-2 bg-emerald-50/60" data-testid="labor-total-row">
                            <span className="text-sm font-medium text-emerald-800">
                              Labor <span className="text-emerald-600 text-xs font-normal">({pitchLabel})</span>
                            </span>
                            <span className={`text-sm font-semibold ${activeVal > 0 ? "text-emerald-800" : "text-slate-300"}`}>
                              {activeVal > 0 ? activeVal.toFixed(2) : "—"}
                            </span>
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
